import {
  Alert,
  Autocomplete,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { strongAutocompletePaperProps } from "components/common/dropdownPaper";
import {
  StepCatalogEntry,
  useGetStepCatalogQuery,
} from "features/integrationsBeta/integrationsBeta";
import { useGetIntegrationStepStagesQuery } from "features/integrationsBeta/platformEnums";
import { useMemo, useState } from "react";
import { humanizeKey } from "utils/humanizeKey";
import ConnectionBindingField from "./ConnectionBindingField";
import JsonCodeField from "./JsonCodeField";
import JsonSchemaForm from "./JsonSchemaForm";

type Json = Record<string, unknown>;

export interface StepFormValues {
  entry: StepCatalogEntry;
  name: string;
  stage: string;
  stage_order: number;
  connection: number | null;
  config: Json;
}

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: number;
  nextStageOrder: number;
  // Edit mode (wizard drafts): prefill and lock the picked catalog entry.
  initial?: StepFormValues | null;
  title?: string;
  submitLabel?: string;
  submitting?: boolean;
  submitError?: string | null;
  onSubmit: (values: StepFormValues) => void;
}

export function humanizeStepKey(stepKey: string): string {
  return humanizeKey(stepKey.split(".").pop() ?? stepKey);
}

/**
 * The step form shared by Settings (AddStepDialog, immediate POST) and the
 * registration wizard (draft mode): searchable catalog picker grouped by
 * stage, prefilled name/stage/order, requirement-driven connection binding
 * (connections are created live - they are client-scoped and reusable), and
 * a schema-driven config form. The caller persists the values.
 */
export default function StepFormDialog({
  open,
  onClose,
  clientId,
  nextStageOrder,
  initial = null,
  title,
  submitLabel,
  submitting = false,
  submitError = null,
  onSubmit,
}: Props) {
  const { data: catalog = [] } = useGetStepCatalogQuery();
  const { data: stages = [] } = useGetIntegrationStepStagesQuery();

  const [entry, setEntry] = useState<StepCatalogEntry | null>(
    initial?.entry ?? null,
  );
  const [name, setName] = useState(initial?.name ?? "");
  const [stage, setStage] = useState(initial?.stage ?? "");
  // Free-text draft (no number-input spinners); parsed on submit.
  const [stageOrderDraft, setStageOrderDraft] = useState(
    String(initial?.stage_order ?? nextStageOrder),
  );
  const stageOrderNum = parseInt(stageOrderDraft, 10);
  const orderValid = Number.isInteger(stageOrderNum) && stageOrderNum >= 1;
  const [connection, setConnection] = useState<number | null>(
    initial?.connection ?? null,
  );
  const [configDraft, setConfigDraft] = useState<Json>(initial?.config ?? {});
  const [rawConfig, setRawConfig] = useState(
    initial && !initial.entry.config_schema
      ? JSON.stringify(initial.config, null, 2)
      : "{}",
  );
  const [rawError, setRawError] = useState<string | null>(null);

  // Group options by suggested stage in pipeline order (MUI groupBy needs
  // contiguous groups; alphabetical would put DESTINATION first).
  const stageOrderIndex = useMemo(() => {
    const index: Record<string, number> = {};
    stages.forEach((s, i) => {
      index[s.value] = i;
    });
    return index;
  }, [stages]);

  const options = useMemo(
    () =>
      [...catalog].sort((a, b) => {
        const ga = stageOrderIndex[a.suggested_stage ?? ""] ?? 99;
        const gb = stageOrderIndex[b.suggested_stage ?? ""] ?? 99;
        return ga - gb || a.step_key.localeCompare(b.step_key);
      }),
    [catalog, stageOrderIndex],
  );

  const stageLabel = (value: string | null | undefined) =>
    stages.find((s) => s.value === value)?.label ?? "Other";

  const filterOptions = useMemo(
    () =>
      createFilterOptions<StepCatalogEntry>({
        stringify: (option) =>
          `${humanizeStepKey(option.step_key)} ${option.step_key}`,
      }),
    [],
  );

  const handlePick = (picked: StepCatalogEntry | null) => {
    setEntry(picked);
    setConnection(null);
    setConfigDraft({});
    setRawConfig("{}");
    setRawError(null);
    if (picked) {
      // Default name = humanized last segment of the step key.
      setName(humanizeStepKey(picked.step_key));
      setStage(picked.suggested_stage ?? "");
      setStageOrderDraft(String(nextStageOrder));
    }
  };

  // null = declared "no connection"; undefined = old catalog without metadata.
  const requirement = entry?.connection_requirement;
  const connectionMissing = Boolean(requirement) && connection == null;

  const handleSubmit = () => {
    if (!entry || !stage || !name.trim() || connectionMissing || !orderValid) {
      return;
    }
    let config: Json = configDraft;
    if (!entry.config_schema) {
      try {
        config = rawConfig.trim() ? JSON.parse(rawConfig) : {};
      } catch {
        setRawError("Invalid JSON.");
        return;
      }
    }
    onSubmit({
      entry,
      name: name.trim(),
      stage,
      stage_order: stageOrderNum,
      connection,
      config,
    });
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={title ?? (initial ? "Edit step" : "Add step")}
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSubmit}
            disabled={
              submitting ||
              !entry ||
              !stage ||
              !name.trim() ||
              connectionMissing ||
              !orderValid
            }
          >
            {submitLabel ?? (initial ? "Save" : "Add step")}
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Autocomplete
          componentsProps={strongAutocompletePaperProps}
          options={options}
          groupBy={(option) => stageLabel(option.suggested_stage)}
          getOptionLabel={(option) => humanizeStepKey(option.step_key)}
          isOptionEqualToValue={(a, b) => a.step_key === b.step_key}
          filterOptions={filterOptions}
          value={entry}
          disabled={Boolean(initial)}
          onChange={(_, picked) => handlePick(picked)}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.step_key}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2">
                  {humanizeStepKey(option.step_key)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {option.step_key}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Step" />
          )}
        />
        {entry && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", wordBreak: "break-all", mt: -1.5 }}
          >
            {entry.step_key}
          </Typography>
        )}

        {entry && (
          <>
            {entry.requires_event_payload && (
              <Alert severity="info">
                This step consumes the inbound webhook payload — it makes the
                whole integration webhook-triggered (no schedules).
              </Alert>
            )}

            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              helperText={
                "Display name of this step in THIS integration only — other " +
                "integrations using the same step are not affected."
              }
            />

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField
                select
                size="small"
                label="Stage"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                sx={{ flex: 1 }}
              >
                {stages.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                inputMode="numeric"
                label="Order"
                value={stageOrderDraft}
                error={!orderValid && stageOrderDraft !== ""}
                onChange={(e) =>
                  setStageOrderDraft(e.target.value.replace(/[^0-9]/g, ""))
                }
                helperText="Same order = parallel; stages run ascending."
                sx={{ width: 200 }}
              />
            </Box>

            {requirement === null ? (
              <Typography variant="caption" color="text.secondary">
                This step does not use a connection.
              </Typography>
            ) : (
              <ConnectionBindingField
                clientId={clientId}
                requirement={requirement}
                value={connection}
                onChange={setConnection}
                allowNone={requirement === undefined}
              />
            )}

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Step config
              </Typography>
              {entry.config_schema ? (
                <JsonSchemaForm
                  schema={entry.config_schema}
                  value={configDraft}
                  onChange={setConfigDraft}
                />
              ) : (
                <JsonCodeField
                  value={rawConfig}
                  onChange={(v) => {
                    setRawConfig(v);
                    setRawError(null);
                  }}
                  error={rawError}
                  rows={5}
                />
              )}
            </Box>
          </>
        )}

        {submitError && <Alert severity="error">{submitError}</Alert>}
      </Stack>
    </AppDialog>
  );
}
